import { useTranslation } from '@msdk/common'
import { AboutMicroservice } from '@msdk/definitions'
import { Box, COLORS, createComponentWrapperWithRef, styled, Typography, uiTokens } from '@msdk/ui'
import { ChevronRight } from '@mui/icons-material'
import { PhoneNumberIcon } from 'components/Icons/AboutIcons'
import React, { useState } from 'react'

import { AboutIconRenderer } from './AboutIconRenderer'
import { formatLineReturn } from './utils'

const StyledPhoneNumberBox = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  justify-content: flex-start;
`

const StyledManufacturerBox = styled(Box)`
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
`

const StyledAboutSectionMuiTitle = styled(Typography)`
  opacity: 60%;
`
const StyledAboutSectionNovaTitle = styled(Typography)`
  color: ${uiTokens.uiContent.neutral};
`
const StyledAboutSectionTitle = createComponentWrapperWithRef(
  {
    mui: StyledAboutSectionMuiTitle,
    nova: StyledAboutSectionNovaTitle,
  },
  'StyledAboutSectionTitle',
)

const StyledItemMuiTypography = styled(Typography)`
  max-width: 360px;
  white-space: pre-wrap;
  line-height: 180%;
`
const StyledItemNovaTypography = styled(Typography)`
  color: ${uiTokens.uiContent.emphasized};
  font-weight: ${uiTokens.font.fontWeight.medium};
`
const StyledItemTypography = createComponentWrapperWithRef(
  {
    mui: StyledItemMuiTypography,
    nova: StyledItemNovaTypography,
  },
  'StyledItemTypography',
)

const StyledAboutMuiItem = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 20px;
`
const StyledAboutNovaItem = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 20px;
`
const StyledAboutItem = createComponentWrapperWithRef(
  {
    mui: StyledAboutMuiItem,
    nova: StyledAboutNovaItem,
  },
  'StyledAboutItem',
)

const StyledAboutColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-right: 50px;
  width: 600px;
  justify-content: flex-start;
`

const StyledShowMoreMuiActionContainer = styled(Box)`
  display: flex;
  align-items: end;
  padding: 20px;
`
const StyledShowMoreNovaActionContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  width: 100%;
  border-top: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
`
const StyledShowMoreActionContainer = createComponentWrapperWithRef(
  {
    mui: StyledShowMoreMuiActionContainer,
    nova: StyledShowMoreNovaActionContainer,
  },
  'StyledShowMoreActionContainer',
)

const StyledShowMoreMuiTypography = styled(Typography)`
  cursor: pointer;
  color: ${COLORS.primary.main};
  width: 100%;
  font-size: 15px;
  font-weight: 700;
  text-align: right;
`
const StyledShowMoreNovaTypography = styled(Typography)`
  color: ${uiTokens.uiContent.emphasized};
`
const StyledShowMoreTypography = createComponentWrapperWithRef<{ children?: React.ReactNode; event?: () => void }>(
  {
    mui: ({ children, event }) => (
      <StyledShowMoreMuiTypography variant="h2" onClick={event}>
        {children}
      </StyledShowMoreMuiTypography>
    ),
    nova: ({ children }) => <StyledShowMoreNovaTypography variant="body1">{children}</StyledShowMoreNovaTypography>,
  },
  'StyledShowMoreTypography',
)

const StyledChevronDownMuiIcon = styled(ChevronRight)`
  color: ${COLORS.primary.main};
  transform: rotate(90deg);
`
const StyledChevronDownNovaIcon = styled(ChevronRight)`
  color: ${COLORS.primary.main};
  transform: rotate(90deg);
  cursor: pointer;
`
const StyledChevronDownIcon = createComponentWrapperWithRef<{ children?: React.ReactNode; event?: () => void }>(
  {
    mui: () => <StyledChevronDownMuiIcon />,
    nova: ({ event }) => <StyledChevronDownNovaIcon onClick={event} />,
  },
  'StyledChevronDownIcon',
)

const StyledChevronUpMuiIcon = styled(ChevronRight)`
  color: ${COLORS.primary.main};
  transform: rotate(-90deg);
`
const StyledChevronUpNovaIcon = styled(ChevronRight)`
  color: ${COLORS.primary.main};
  transform: rotate(-90deg);
  cursor: pointer;
`
const StyledChevronUpIcon = createComponentWrapperWithRef<{ children?: React.ReactNode; event?: () => void }>(
  {
    mui: () => <StyledChevronUpMuiIcon />,
    nova: ({ event }) => <StyledChevronUpNovaIcon onClick={event} />,
  },
  'StyledChevronUpIcon',
)

const StyledAboutMuiImporterBox = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 800px;
  padding: 30px;
  height: fit-content;
  padding-top: 0;
`
const StyledAboutNovaImporterBox = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 500px;
  padding: 30px;
  height: fit-content;
  padding-top: 0;
`
const StyledAboutImporterBox = createComponentWrapperWithRef(
  {
    mui: StyledAboutMuiImporterBox,
    nova: StyledAboutNovaImporterBox,
  },
  'StyledAboutImporterBox',
)
interface ImporterDetailsProps {
  microservice: AboutMicroservice
}

const ImporterDetails = ({ microservice }: ImporterDetailsProps) => {
  const t = useTranslation()
  const [isShowMoreOpen, setIsShowMoreOpen] = useState(false)

  const handleClickShowMore = () => {
    setIsShowMoreOpen(!isShowMoreOpen)
  }

  return (
    <>
      <StyledShowMoreActionContainer>
        {isShowMoreOpen ? (
          <>
            <StyledShowMoreTypography event={handleClickShowMore}>
              {t('ABOUT_MODAL_SHOW_LESS', 'Show less')}
            </StyledShowMoreTypography>
            <StyledChevronUpIcon event={handleClickShowMore} />
          </>
        ) : (
          <>
            <StyledShowMoreTypography event={handleClickShowMore}>
              {t('ABOUT_MODAL_SHOW_MORE', 'Show more')}
            </StyledShowMoreTypography>
            <StyledChevronDownIcon event={handleClickShowMore} />
          </>
        )}
      </StyledShowMoreActionContainer>
      {isShowMoreOpen && (
        <StyledAboutImporterBox>
          <StyledAboutColumn>
            <StyledAboutItem>
              <AboutIconRenderer iconType="Importer" />
              <StyledManufacturerBox>
                <StyledAboutSectionTitle variant="body2">{t('ABOUT_IMPORTER', 'Importer')}</StyledAboutSectionTitle>
                {microservice.importer?.countryIdentifier && (
                  <StyledItemTypography variant="body1">
                    {formatLineReturn(microservice.importer.countryIdentifier)}
                  </StyledItemTypography>
                )}
                {microservice.importer?.companyName1 && (
                  <StyledItemTypography variant="body1">
                    {formatLineReturn(microservice.importer.companyName1)}
                  </StyledItemTypography>
                )}
                {microservice.importer?.companyName2 && (
                  <StyledItemTypography variant="body1">
                    {formatLineReturn(microservice.importer.companyName2)}
                  </StyledItemTypography>
                )}
                {microservice.importer?.address1 && (
                  <StyledItemTypography variant="body1">
                    {formatLineReturn(microservice.importer.address1)}
                  </StyledItemTypography>
                )}
                {microservice.importer?.address2 && (
                  <StyledItemTypography variant="body1">
                    {formatLineReturn(microservice.importer.address2)}
                  </StyledItemTypography>
                )}
                <StyledItemTypography variant="body1">
                  {microservice.importer?.zipCode && <span>{microservice.importer.zipCode + ' '}</span>}
                  {microservice.importer?.city && <span>{microservice.importer.city + ' '}</span>}
                  {microservice.importer?.province && <span>{microservice.importer.province + ' '}</span>}
                </StyledItemTypography>
                {microservice.importer?.country && (
                  <StyledItemTypography variant="body1">{microservice.importer.country}</StyledItemTypography>
                )}
                {microservice.importer?.phoneNumber && (
                  <StyledPhoneNumberBox>
                    <PhoneNumberIcon />
                    <StyledItemTypography variant="body1">{microservice.importer.phoneNumber}</StyledItemTypography>
                  </StyledPhoneNumberBox>
                )}
              </StyledManufacturerBox>
            </StyledAboutItem>
          </StyledAboutColumn>
        </StyledAboutImporterBox>
      )}
    </>
  )
}

export default ImporterDetails
